// Utility module for handling server communications
// Manages API calls with authentication
import { auth } from '../firebase-config';

// Base API URL with fallback to production
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://clickheard.uc.r.appspot.com';

// Helper function to make authenticated API calls
const fetchWithAuth = async (url, options = {}) => {
  // Get current user and authentication token
  const user = auth.currentUser;
  const token = user ? await user.getIdToken() : null;
  
  // Debug logging
  console.log('Making authenticated request:', {
    url,
    method: options.method || 'GET',
    token: token ? 'present' : 'missing'
  });
  
  // Throw error if no authentication token is available
  if (!token) {
    throw new Error('No authentication token available');
  }

  // Set up headers with authentication token
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
    ...options.headers,
  };

  try {
    // Make the API call
    const response = await fetch(url, {
      ...options,
      headers,
    });

    // Handle non-200 responses
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Server response error:', {
        status: response.status,
        statusText: response.statusText,
        body: errorText,
        url,
        method: options.method || 'GET'
      });
      throw new Error(`API call failed: ${response.statusText} - ${errorText}`);
    }

    return response;
  } catch (error) {
    console.error('Fetch error:', error);
    throw error;
  }
};

// Wrapper functions for common HTTP methods
export async function get(endpoint, authToken = null) {
  return fetchWithAuth(`${API_BASE_URL}${endpoint}`, {
    method: 'GET',
  });
}

export async function post(endpoint, data, authToken = null) {
  return fetchWithAuth(`${API_BASE_URL}${endpoint}`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function createSurvey() {
  console.log('Initiating create survey request');
  try {
    // Use the dedicated create endpoint instead of the general surveys endpoint
    const response = await post('/api/surveys/create');
    console.log('Create survey response:', response);
    const data = await response.json();
    return data;  // This will return the full survey object including UUID
  } catch (error) {
    console.error('Create survey error:', error);
    throw error;
  }
}

export const saveSurvey = async (surveyId, surveyData) => {
  // Only include surveyId in the data if it exists and is not undefined
  const dataToSend = surveyId 
    ? { ...surveyData, surveyId } 
    : surveyData;

  // Always use the same endpoint
  const response = await fetchWithAuth(`${API_BASE_URL}/api/surveys`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(dataToSend),
  });

  if (!response.ok) {
    const errorData = await response.json();
    console.error('Server returned error:', errorData);
    throw new Error(errorData.message || 'Failed to save survey');
  }

  return response.json();
};

export const deleteSurvey = async (surveyId) => {
  const user = auth.currentUser;
  const token = user ? await user.getIdToken() : null;
  
  if (!token) {
    throw new Error('No authentication token available');
  }

  const response = await fetch(`${API_BASE_URL}/api/surveys/${surveyId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
  
  if (!response.ok) {
    throw new Error('Failed to delete survey');
  }
  
  return response.json();
};

export const fetchSurveys = async () => {
  try {
    const response = await get('/api/surveys');
    return response.json();
  } catch (error) {
    console.error('Error fetching surveys:', error);
    return [];
  }
};

export const fetchSurveyById = async (surveyId) => {
  try {
    // Use direct fetch instead of authenticated get()
    const response = await fetch(`${API_BASE_URL}/api/s/${surveyId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch survey');
    }

    return response.json();
  } catch (error) {
    console.error('Error fetching survey:', error);
    throw error;
  }
};

export async function submitSurveyResponses(surveyId, responses, respondentId) {
  try {
    // Get auth token if user is signed in
    const user = auth.currentUser;
    const headers = {
      'Content-Type': 'application/json'
    };
    
    // Only add Authorization header if user is authenticated
    if (user) {
      const token = await user.getIdToken();
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}/api/responses`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        surveyId,
        responses,
        respondentId
      })
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to submit responses');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error submitting responses:', error);
    throw error;
  }
}

export const fetchSurveyResponseCount = async (surveyId) => {
  try {
    const response = await get(`/api/surveys/${surveyId}/responses/count`);
    return response.json();
  } catch (error) {
    console.error('Error fetching response count:', error);
    return 0;
  }
};

export const checkUserResponse = async (surveyId) => {
  try {
    const response = await get(`/api/surveys/${surveyId}/user-response`);
    const data = await response.json();
    return data.hasResponded;
  } catch (error) {
    console.error('Error checking user response:', error);
    return false;
  }
};

export const fetchSurveyAnalytics = async (surveyId) => {
  try {
    const response = await get(`/api/surveys/analytics/${surveyId}`);
    if (!response.ok) {
      throw new Error('Failed to fetch survey analytics');
    }
    return response.json();
  } catch (error) {
    console.error('Error fetching survey analytics:', error);
    throw error;
  }
};
